import React, { Component } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { SignUpLink } from '../SignUp';
import { PasswordForgetLink } from '../PasswordForget';
import { withFirebase } from '../Firebase';
import * as ROUTES from '../../constants/routes';


const withRouter = (Component) => {
  return (props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();

    return <Component {...props} router={{ navigate, location, params }} />;
  };
};

const withTranslation = (Component) => {
  return (props) => {
    const { t } = useTranslation();
    return <Component {...props} t={t} />;
  };
};

const SignInPage = () => {
  const { t } = useTranslation();
  
  return (
    <main className='auth-main'>
      <div className='auth-left'>
        <div className='auth-card'>
          <h1>{t('signIn.title')}</h1>
          <SignInForm />
          <div className='auth-divider'>
            <span>{t('signIn.or_sign_in_with')}</span>
          </div>
          <div className='auth-providers'>
            <SignInGoogle />
            <SignInMicrosoft />
          </div>
          <div className='auth-footer'>
            <PasswordForgetLink />
            <SignUpLink />
          </div>
        </div>
      </div>
      <div className='auth-right'>
        <img 
          src="https://picsum.photos/1200/1200"
          alt={t('signIn.construction_image_alt')}
          loading="lazy"
          sizes="(max-width: 600px) 200px, (max-width: 1200px) 400px, 800px"
        />
      </div>
    </main>
  );
};

const INITIAL_STATE = {
  email: '',
  password: '',
  submitting: false,
  error: null,
};

const ERROR_CODE_ACCOUNT_EXISTS =
  'auth/account-exists-with-different-credential';

const ERROR_MSG_ACCOUNT_EXISTS = `
  An account with an E-Mail address to
  this social account already exists. Try to login from
  this account instead and associate your social accounts on
  your personal account page.
`;

class SignInFormBase extends Component {
  constructor(props) {
    super(props);

    this.state = { ...INITIAL_STATE };
  }

  onSubmit = event => {
    event.preventDefault();
    if (this.state.submitting) return;
    const { email, password } = this.state;

    this.setState({ submitting: true, error: null });

    this.props.firebase
      .doSignInWithEmailAndPassword(email, password, null, email)
      .then(() => {
        this.setState({ ...INITIAL_STATE });
        this.props.router.navigate(ROUTES.HOME);
      })
      .catch(error => {
        this.setState({ error });
      })
      .finally(() => {
        this.setState(prevState => (
          prevState.submitting ? { submitting: false } : null
        ));
      });
  };

  onChange = event => {
    this.setState({ [event.target.name]: event.target.value });
  };

  render() {
    const { email, password, submitting, error } = this.state;
    const { t } = this.props;

    const isInvalid = password === '' || email === '';

    return (
      <form onSubmit={this.onSubmit} className='auth-form'>
        {error && (
          <p className='auth-error'>{error.message}</p>
        )}
        <input
          name="email"
          value={email}
          onChange={this.onChange}
          type="email"
          placeholder={t('signIn.email_placeholder')}
          aria-label={t('email')}
          autoComplete="username"
          required

        />
        <input
          name="password"
          value={password}
          onChange={this.onChange}
          type="password"
          placeholder={t('signIn.password_placeholder')}
          aria-label={t('password')}
          autoComplete="current-password"
          required
        />
        <button disabled={isInvalid || submitting} type="submit" className={submitting ? 'btn-loading' : undefined}>
          {t('signIn.submit_button')}
        </button>
      </form>
    );
  }
}

class SignInGoogleBase extends Component {
  constructor(props) {
    super(props);

    this.state = { error: null, submitting: false };
  }

  onSubmit = event => {
    event.preventDefault();
    if (this.state.submitting) return;

    this.setState({ submitting: true, error: null });

    this.props.firebase
      .doSignInWithGoogle()
      .then(socialAuthUser => {
        // Create or update a user in your Firebase Realtime Database.
        // Using `update` instead of `set` will preserve existing data like roles.
        if (socialAuthUser.additionalUserInfo.isNewUser) {
          return this.props.firebase.user(socialAuthUser.user.uid).set({
            username: socialAuthUser.user.displayName,
            email: socialAuthUser.user.email,
            roles: {}, // Set default roles for new users if needed
          });
        }
        return this.props.firebase.user(socialAuthUser.user.uid).update({
          username: socialAuthUser.user.displayName,
          email: socialAuthUser.user.email,
        });
      })
      .then(() => {
        this.setState({ error: null });
        this.props.router.navigate(ROUTES.HOME);
      })
      .catch(error => {
        if (error.code === ERROR_CODE_ACCOUNT_EXISTS) {
          error.message = ERROR_MSG_ACCOUNT_EXISTS;
        }

        this.setState({ error });
      })
      .finally(() => {
        this.setState(prevState => (
          prevState.submitting ? { submitting: false } : null
        ));
      });
  };

  render() {
    const { error, submitting } = this.state;
    const { t } = this.props;

    return (
      <div className="inline">
        <button className={'gsi-material-button' + (submitting ? ' btn-loading' : '')} onClick={this.onSubmit} type="button" disabled={submitting}>
          <div className="gsi-material-button-state"></div>
          <div className="gsi-material-button-content-wrapper">
            <div className="gsi-material-button-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 48 48"
                style={{ display: "block" }}
              >
                <path
                  fill="#EA4335"
                  d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                ></path>
                <path
                  fill="#4285F4"
                  d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                ></path>
                <path
                  fill="#FBBC05"
                  d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                ></path>
                <path
                  fill="#34A853"
                  d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                ></path>
                <path fill="none" d="M0 0h48v48H0z"></path>
              </svg>
            </div>
            <span className="gsi-material-button-contents">{t('signIn.sign_in_google')}</span>
            <span style={{ display: "none" }}>{t('signIn.sign_in_google')}</span>
          </div>
        </button>
        {error && (
          <p className='auth-error'>{error.message}</p>
        )}
      </div>
    );
  }
}

class SignInMicrosoftBase extends Component {
  constructor(props) {
    super(props);

    this.state = { error: null, submitting: false };
  }

  onSubmit = event => {
    event.preventDefault();
    if (this.state.submitting) return;

    this.setState({ submitting: true, error: null });

    this.props.firebase
      .doSignInWithMicrosoft()
      .then(socialAuthUser => {
        // Create or update a user in your Firebase Realtime Database.
        // Using `update` instead of `set` will preserve existing data like roles.
        if (socialAuthUser.additionalUserInfo.isNewUser) {
          return this.props.firebase.user(socialAuthUser.user.uid).set({
            username: socialAuthUser.user.displayName,
            email: socialAuthUser.user.email,
            roles: {}, // Set default roles for new users if needed
          });
        }
        return this.props.firebase.user(socialAuthUser.user.uid).update({
          username: socialAuthUser.user.displayName,
          email: socialAuthUser.user.email,
        });
      })
      .then(() => {
        this.setState({ error: null });
        this.props.router.navigate(ROUTES.HOME);
      })
      .catch(error => {
        if (error.code === ERROR_CODE_ACCOUNT_EXISTS) {
          error.message = this.props.t('signIn.error_account_exists');
        }

        this.setState({ error });
      })
      .finally(() => {
        this.setState(prevState => (
          prevState.submitting ? { submitting: false } : null
        ));
      });
  };

  render() {
    const { error, submitting } = this.state;
    const { t } = this.props;

    return (
      <div className="inline">
        <button className={'microsoft-button' + (submitting ? ' btn-loading' : '')} onClick={this.onSubmit} type="button" disabled={submitting}>
          <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21">
            <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
            <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
            <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
            <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
          </svg>
          <span>{t('signIn.sign_in_microsoft')}</span>
        </button>
        {error && (
          <p className='auth-error'>{error.message}</p>
        )}
      </div>
    );
  }
}

class SignInFacebookBase extends Component {
  constructor(props) {
    super(props);

    this.state = { error: null };
  }

  onSubmit = event => {
    this.props.firebase
      .doSignInWithFacebook()
      .then(socialAuthUser => {
        // Create a user in your Firebase Realtime Database too
        return this.props.firebase.user(socialAuthUser.user.uid).set({
          username: socialAuthUser.additionalUserInfo.profile.name,
          email: socialAuthUser.additionalUserInfo.profile.email,
        });
      })
      .then(() => {
        this.setState({ error: null });
        this.props.router.navigate(ROUTES.HOME);
      })
      .catch(error => {
        if (error.code === ERROR_CODE_ACCOUNT_EXISTS) {
          error.message = ERROR_MSG_ACCOUNT_EXISTS;
        }

        this.setState({ error });
      });

    event.preventDefault();
  };

  render() {
    const { error } = this.state;

    return (
      <form onSubmit={this.onSubmit} className="inline" >
        <button color="facebook" type="submit" disabled={true} >
          {/* <icon name="facebook" />  */}
          Facebook
        </button>

        {error && (
          <p>{error.message}</p>
        )}
      </form>
    );
  }
}

class SignInTwitterBase extends Component {
  constructor(props) {
    super(props);

    this.state = { error: null };
  }

  onSubmit = event => {
    this.props.firebase
      .doSignInWithTwitter()
      .then(socialAuthUser => {
        // Create a user in your Firebase Realtime Database too
        return this.props.firebase.user(socialAuthUser.user.uid).set({
          username: socialAuthUser.additionalUserInfo.profile.name,
          email: socialAuthUser.additionalUserInfo.profile.email,
        });
      })
      .then(() => {
        this.setState({ error: null });
        this.props.router.navigate(ROUTES.HOME);
      })
      .catch(error => {
        if (error.code === ERROR_CODE_ACCOUNT_EXISTS) {
          error.message = ERROR_MSG_ACCOUNT_EXISTS;
        }

        this.setState({ error });
      });

    event.preventDefault();
  };

  render() {
    const { error } = this.state;

    return (
      <form onSubmit={this.onSubmit} className="inline">
        <button color="twitter" type="submit" disabled={true}>
          {/* <Icon name="twitter" />  */}
          Twitter
        </button>

        {error && (
          <p>{error.message}</p>
        )}
      </form>
    );
  }
}

const SignInForm = withTranslation(withRouter(withFirebase(SignInFormBase)));

const SignInGoogle = withTranslation(withRouter(withFirebase(SignInGoogleBase)));

const SignInMicrosoft = withTranslation(withRouter(withFirebase(SignInMicrosoftBase)));

const SignInFacebook = withRouter(withFirebase(SignInFacebookBase));

const SignInTwitter = withRouter(withFirebase(SignInTwitterBase));

export default SignInPage;

export { SignInForm, SignInGoogle, SignInMicrosoft, SignInFacebook, SignInTwitter };
